/**
 * Copilot-generated rangeSlider widget that updates a rangeInput widget as you drag it
 */
import { connectRange } from 'instantsearch.js/es/connectors';
import Pit from 'instantsearch.js/es/components/Slider/Pit';
import Rheostat from 'instantsearch.js/es/components/Slider/Rheostat';
import { h, render, Component, ComponentType } from 'preact';

type Bounds = [number, number];

type Container = string | HTMLElement;

type LiveRangeSliderParams = {
  attribute: string;
  container: Container;
  inputsContainer: Container;
  cssClasses?: {
    root?: string;
    disabledRoot?: string;
  };
  min?: number;
  max?: number;
  pips?: boolean;
  precision?: number;
  step?: number;
  tooltips?: boolean | {
    format?: (rawValue: number) => string | number;
  };
};

const RheostatComponent = Rheostat as unknown as ComponentType<any>;
const PitComponent = Pit as unknown as ComponentType<any>;

const getContainerNode = (container: Container): HTMLElement => {
  if (typeof container === 'string') {
    const node = document.querySelector<HTMLElement>(container);

    if (!node) {
      throw new Error(`Container not found: ${container}`);
    }

    return node;
  }

  return container;
};

const buildRange = (start: number, end: number, step = 1): number[] => {
  const values: number[] = [];

  for (let value = start; value < end; value += step) {
    values.push(value);
  }

  return values;
};

const roundValue = (value: number): number => Math.round(value * 100) / 100;

const updateRangeInputs = (inputsContainerNode: HTMLElement, values: Bounds) => {
  const inputs = inputsContainerNode.querySelectorAll<HTMLInputElement>('input.ais-RangeInput-input');

  if (inputs.length < 2) {
    return;
  }

  const nextValues = values.map((value) => `${roundValue(value)}`) as [string, string];

  inputs.forEach((input, index) => {
    if (document.activeElement === input) {
      return;
    }

    input.value = nextValues[index];
  });
};

class LiveSlider extends Component<any> {
  private handleChange = ({ values }) => {
    if (!this.isDisabled) {
      this.props.refine(values);
    }
  };

  private handleValuesUpdated = ({ values }) => {
    this.props.onValuesUpdated?.(values as Bounds);
  };

  public get isDisabled() {
    return this.props.min >= this.props.max;
  }

  private computeDefaultPitPoints({ min, max }): number[] {
    const totalLength = max - min;
    const steps = 34;
    const stepsLength = totalLength / steps;

    return [min, ...buildRange(0, steps - 1).map((step) => min + stepsLength * (step + 1)), max];
  }

  private computeSnapPoints({ min, max, step }): number[] | undefined {
    if (!step) {
      return undefined;
    }

    return [...buildRange(min, max, step), max];
  }

  private createHandleComponent(tooltips) {
    return (props) => {
      const roundedValue = roundValue(parseFloat(`${props['aria-valuenow']}`));
      const value = typeof tooltips === 'object' && tooltips.format
        ? tooltips.format(roundedValue)
        : roundedValue;
      const handleKey = props['data-handle-key'];
      const className = [
        props.className,
        handleKey === 0 ? 'rheostat-handle-lower' : '',
        handleKey === 1 ? 'rheostat-handle-upper' : '',
      ].filter(Boolean).join(' ');
      const ariaLabel = handleKey === 0 ? 'Minimum Filter Handle' : 'Maximum Filter Handle';

      return h('div', {
        ...props,
        className,
        'aria-label': ariaLabel,
      }, tooltips && h('div', { className: 'rheostat-tooltip' }, value));
    };
  }

  public render() {
    const {
      tooltips,
      step,
      pips,
      values,
      cssClasses,
      min: originalMin,
      max: originalMax,
    } = this.props;

    const { min, max } = this.isDisabled
      ? { min: originalMin, max: originalMax + 0.001 }
      : this.props;

    const snapPoints = this.computeSnapPoints({ min, max, step });
    const pitPoints = pips === false
      ? []
      : this.computeDefaultPitPoints({ min, max });

    return h('div', {
      className: [cssClasses.root, this.isDisabled ? cssClasses.disabledRoot : ''].filter(Boolean).join(' '),
    }, h(RheostatComponent, {
      handle: this.createHandleComponent(tooltips),
      onChange: this.handleChange,
      onValuesUpdated: this.handleValuesUpdated,
      min,
      max,
      pitComponent: PitComponent,
      pitPoints,
      snap: true,
      snapPoints,
      values: this.isDisabled ? [min, max] : values,
      disabled: this.isDisabled,
    }));
  }
}

const renderer = ({
  containerNode,
  cssClasses,
  inputsContainerNode,
  pips,
  step,
  tooltips,
}) => ({ refine, range, start }, isFirstRendering) => {
  if (isFirstRendering) {
    return;
  }

  const minRange = range.min;
  const maxRange = range.max;
  const [minStart, maxStart] = start;
  const minFinite = minStart === -Infinity ? minRange : minStart;
  const maxFinite = maxStart === Infinity ? maxRange : maxStart;
  const values: Bounds = [
    minFinite > maxRange ? maxRange : minFinite,
    maxFinite < minRange ? minRange : maxFinite,
  ];

  updateRangeInputs(inputsContainerNode, values);

  render(h(LiveSlider, {
    cssClasses,
    max: maxRange,
    min: minRange,
    onValuesUpdated(nextValues: Bounds) {
      updateRangeInputs(inputsContainerNode, nextValues);
    },
    pips,
    refine,
    step,
    tooltips,
    values,
  }), containerNode);
};

export default function liveRangeSlider(widgetParams: LiveRangeSliderParams) {
  const {
    attribute,
    container,
    inputsContainer,
    cssClasses: userCssClasses = {},
    min,
    max,
    pips = true,
    precision = 0,
    step,
    tooltips = true,
  } = widgetParams || {};

  const containerNode = getContainerNode(container);
  const inputsContainerNode = getContainerNode(inputsContainer);
  const cssClasses = {
    root: ['ais-RangeSlider', userCssClasses.root].filter(Boolean).join(' '),
    disabledRoot: ['ais-RangeSlider--disabled', userCssClasses.disabledRoot].filter(Boolean).join(' '),
  };

  return {
    ...connectRange(renderer({
      containerNode,
      cssClasses,
      inputsContainerNode,
      pips,
      step,
      tooltips,
    }), () => render(null, containerNode))({
      attribute,
      max,
      min,
      precision,
    }),
    $$type: 'ais.liveRangeSlider',
    $$widgetType: 'ais.liveRangeSlider',
  };
}
